import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Chip, IconButton } from '@mui';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { saveDataType } from '@actions/data-types-actions';
import { pushApplicationMessage } from '@actions/messages-actions';
import { fetchDataDetail } from '@actions/data-detail-actions';
import * as colors from '@constants/colors';
import './top-bar-action-entity-type-icons.scss';

const TopBarActionEntityTypeIcons = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const { data } = useSelector(state => state.dataDetail);

  const getParam = useCallback(name => new URLSearchParams(location.search).get(name), [location.search]);
  const isPreview = getParam('mode') === 'preview';
  const isSettingsOpen = getParam('panel') === 'settings';

  const setUrlParam = useCallback((param, value) => {
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set(param, value);
    } else {
      params.delete(param);
    }
    const search = params.toString();
    history.replace(search ? `${location.pathname}?${search}` : location.pathname);
  }, [history, location]);

  const handleSaveDraft = useCallback(() => {
    if (!data) {
      return;
    }
    dispatch(saveDataType('map_type', id, { is_public: false }, () => {
      dispatch(fetchDataDetail('map_type', id));
      dispatch(pushApplicationMessage('Draft saved.'));
    }));
  }, [dispatch, id, data]);

  const handlePublish = useCallback(() => {
    if (!data) {
      return;
    }
    dispatch(saveDataType('map_type', id, { is_public: true }, () => {
      dispatch(fetchDataDetail('map_type', id));
      dispatch(pushApplicationMessage('Published.'));
    }));
  }, [dispatch, id, data]);

  const handlePreview = useCallback(() => {
    if (isPreview) {
      setUrlParam('mode', null);
    } else {
      setUrlParam('mode', 'preview');
    }
  }, [isPreview, setUrlParam]);

  const handleSettings = useCallback(() => {
    if (isSettingsOpen) {
      setUrlParam('panel', null);
    } else {
      setUrlParam('panel', 'settings');
    }
  }, [isSettingsOpen, setUrlParam]);

  if (!data) {
    return null;
  }

  return (
    <div styleName="entity-type-icons-container">
      <Chip
        label={data.is_public ? 'Published' : 'Draft'}
        size="small"
        style={{
          backgroundColor: data.is_public ? '#E8F5E9' : '#F5F5F5',
          color: data.is_public ? '#2E7D32' : '#757575',
          fontWeight: 500,
          fontSize: '0.75rem',
          marginRight: '0.75rem'
        }}
      />
      <Button
        plain
        size="small"
        variant="outlined"
        onClick={handleSaveDraft}
        styleName="action-btn"
      >
        Save Draft
      </Button>
      <Button
        plain
        color="primary"
        size="small"
        variant="contained"
        onClick={handlePublish}
        styleName="action-btn action-btn-publish"
      >
        Publish
      </Button>
      <IconButton size="small" onClick={handlePreview} tooltip="Preview" aria-label="Preview">
        <VisibilityIcon
          fontSize="small"
          htmlColor={isPreview ? colors.dotmapsBlue : colors.dotmapsGray}
        />
      </IconButton>
      <IconButton size="small" tooltip="Version history" aria-label="Version history">
        <HistoryIcon fontSize="small" htmlColor={colors.dotmapsGray} />
      </IconButton>
      <IconButton size="small" onClick={handleSettings} tooltip="Settings" aria-label="Settings">
        <SettingsIcon
          fontSize="small"
          htmlColor={isSettingsOpen ? colors.dotmapsBlue : colors.dotmapsGray}
        />
      </IconButton>
      <IconButton size="small" tooltip="More" aria-label="More">
        <MoreVertIcon fontSize="small" htmlColor={colors.dotmapsGray} />
      </IconButton>
    </div>
  );
};

export default TopBarActionEntityTypeIcons;
